import { useState } from 'react'
import { Bot, Maximize2, Minimize2, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TabBar, type TabBarItem } from '@/components/TabBar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TestCaseStatusBadge } from '@/components/projects/TestCaseStatusBadge'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { useTestCase, useTestRunsByTestCase } from '@/hooks'
import { cn, getInitials } from '@/lib/utils'
import { TestRunStatuses, type TestCaseDetail, type TestRun } from '@/types'
import { TestCaseAutomationTab } from './TestCaseAutomationTab'
import { TestCaseRunsTab } from './TestCaseRunsTab'

type DetailTab = 'overview' | 'automation' | 'runs'

const RUNS_PAGE = { maxResultCount: 50, skipCount: 0 }

interface TestCaseDetailPanelProps {
  projectId: string
  testCaseId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TestCaseDetailPanel({
  projectId,
  testCaseId,
  open,
  onOpenChange,
}: TestCaseDetailPanelProps) {
  const { data: testCase, isLoading } = useTestCase(projectId, testCaseId)

  const [fullScreen, setFullScreen] = useState(false)

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) setFullScreen(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className={cn(
          'fixed top-0 right-0 left-auto translate-x-0 translate-y-0 h-full max-h-screen p-0 gap-0 border-l rounded-none flex flex-col bg-background',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
          fullScreen
            ? 'w-screen max-w-none'
            : 'w-full sm:max-w-[800px] sm:w-[800px]',
        )}
      >
        <DialogTitle className="sr-only">
          {testCase ? `${testCase.testCaseIdentifier}: ${testCase.title}` : 'Test case details'}
        </DialogTitle>

        {isLoading || !testCase ? (
          <PanelLoading />
        ) : (
          <PanelBody
            testCase={testCase}
            fullScreen={fullScreen}
            onToggleFullScreen={() => setFullScreen((v) => !v)}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PanelLoading() {
  return (
    <div className="flex flex-col h-full" role="status" aria-live="polite" aria-label="Loading test case">
      <div className="px-6 py-4 border-b flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-6 flex-1" />
        <Skeleton className="h-8 w-20 rounded" />
      </div>
      <div className="p-6 space-y-4 flex-1">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  )
}

interface PanelBodyProps {
  testCase: TestCaseDetail
  fullScreen: boolean
  onToggleFullScreen: () => void
  onClose: () => void
}

function PanelBody({
  testCase,
  fullScreen,
  onToggleFullScreen,
  onClose,
}: PanelBodyProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview')

  const { data: runsData } = useTestRunsByTestCase(testCase.projectId, testCase.id, RUNS_PAGE)
  const runsCount = runsData?.totalCount ?? 0
  const latestRun = runsData?.items?.[0]

  const tabs: TabBarItem<DetailTab>[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'automation', label: 'Automation' },
    { key: 'runs', label: 'Runs', badge: runsCount > 0 ? runsCount : undefined },
  ]

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-6 py-4 border-b flex items-center gap-3 shrink-0">
        <button
          type="button"
          className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
          onClick={onClose}
          aria-label="Close test case panel"
          data-testid="test-case-detail-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2
            className="text-lg font-bold truncate"
            title={`${testCase.testCaseIdentifier}: ${testCase.title}`}
            data-testid="test-case-detail-title"
          >
            {testCase.testCaseIdentifier}: {testCase.title}
          </h2>
          {(testCase.isAutomated || latestRun) && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              {testCase.isAutomated && <AutomatedHeaderBadge automationStatus={testCase.automationStatus} />}
              {latestRun && <LastRunHeaderBadge run={latestRun} />}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={onToggleFullScreen}
          aria-label={fullScreen ? 'Collapse panel' : 'Expand panel to full screen'}
          data-testid="test-case-detail-toggle-fullscreen"
        >
          {fullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>
      </div>

      <div className="px-6 pt-4 shrink-0">
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
        {activeTab === 'overview' && (
          <>
            <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
              <StackedField label="Test Suite">
                <span className="font-medium" data-testid="test-case-detail-test-suite">
                  {testCase.testSuiteTitle}
                </span>
              </StackedField>
              <StackedField label="Status">
                <span data-testid="test-case-detail-status">
                  <TestCaseStatusBadge status={testCase.status} />
                </span>
              </StackedField>
              <StackedField label="Assignee">
                {testCase.assigneeName ? (
                  <div
                    className="flex items-center gap-2"
                    data-testid="test-case-detail-assignee"
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                      {getInitials(testCase.assigneeName)}
                    </div>
                    <span className="font-medium">{testCase.assigneeName}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground" data-testid="test-case-detail-assignee">
                    Unassigned
                  </span>
                )}
              </StackedField>
            </div>

            <LinkedWorkItemsSection testCase={testCase} />

            <DescriptionSection testCase={testCase} />

            <TestStepsSection testCase={testCase} />
          </>
        )}

        {activeTab === 'automation' && (
          <TestCaseAutomationTab
            projectId={testCase.projectId}
            testCaseId={testCase.id}
            testCaseIdentifier={testCase.testCaseIdentifier}
            isAutomated={testCase.isAutomated}
            automationStatus={testCase.automationStatus}
          />
        )}

        {activeTab === 'runs' && (
          <TestCaseRunsTab projectId={testCase.projectId} testCaseId={testCase.id} />
        )}
      </div>
    </div>
  )
}

function StackedField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      {children}
    </div>
  )
}

function LinkedWorkItemsSection({ testCase }: Readonly<{ testCase: TestCaseDetail }>) {
  const items = testCase.linkedWorkItems
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Linked Work Items
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="test-case-detail-no-linked-work-item">
          No linked work items.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((wi) => (
            <Card
              key={wi.id}
              className="flex items-center gap-3 p-3 shadow-none"
              data-testid={`test-case-detail-linked-work-item-${wi.id}`}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted shrink-0">
                <span className="text-xs font-bold text-muted-foreground">WI</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {wi.number !== null ? `WI-${String(wi.number).padStart(3, '0')}: ` : ''}
                  {wi.title}
                </p>
              </div>
              <WorkItemStatusBadge status={wi.status} />
            </Card>
          ))}
        </div>
      )}
    </section>
  )
}

function DescriptionSection({ testCase }: Readonly<{ testCase: TestCaseDetail }>) {
  const description = testCase.description?.trim() ?? ''
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Description
      </h3>
      <Card className="p-3 shadow-none" data-testid="test-case-detail-description">
        {description.length > 0 ? (
          <p className="text-sm whitespace-pre-wrap break-words">{description}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No description.</p>
        )}
      </Card>
    </section>
  )
}

function TestStepsSection({ testCase }: { testCase: TestCaseDetail }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Test Steps
      </h3>
      {testCase.steps.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="test-case-detail-no-steps">
          No test steps.
        </p>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Test steps" data-testid="test-case-detail-steps">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">#</TableHead>
                <TableHead>Step Name</TableHead>
                <TableHead>Expected Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testCase.steps.map((step) => (
                <TableRow
                  key={step.stepNumber}
                  data-testid={`test-case-detail-step-${step.stepNumber}`}
                >
                  <TableCell className="align-top text-muted-foreground">
                    {step.stepNumber}
                  </TableCell>
                  <TableCell className="align-top whitespace-pre-wrap">{step.description}</TableCell>
                  <TableCell className="align-top whitespace-pre-wrap">
                    <span data-testid={`test-case-detail-step-expected-${step.stepNumber}`}>{step.expectedResult ?? ''}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function AutomatedHeaderBadge({ automationStatus }: Readonly<{ automationStatus: string }>) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-purple-foreground bg-purple px-2 py-0.5 text-xs font-medium text-purple-foreground"
      title={`Automated · ${automationStatus}`}
      data-testid="test-case-detail-automated-badge"
    >
      <Bot className="h-3 w-3" aria-hidden="true" />
      Automated
    </span>
  )
}

const LAST_RUN_STYLES: Record<string, string> = {
  [TestRunStatuses.Passed]: 'border-success-foreground bg-success text-success-foreground',
  [TestRunStatuses.Failed]: 'border-danger-foreground bg-danger text-danger-foreground',
}
const LAST_RUN_DEFAULT = 'border-warning-foreground bg-warning text-warning-foreground'
const LAST_RUN_DOT: Record<string, string> = {
  [TestRunStatuses.Passed]: 'bg-green-500',
  [TestRunStatuses.Failed]: 'bg-red-500',
}
const LAST_RUN_DOT_DEFAULT = 'bg-amber-500'

function LastRunHeaderBadge({ run }: Readonly<{ run: TestRun }>) {
  const styles = LAST_RUN_STYLES[run.status] ?? LAST_RUN_DEFAULT
  const dot = LAST_RUN_DOT[run.status] ?? LAST_RUN_DOT_DEFAULT
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium', styles)}
      title={new Date(run.runAt).toLocaleString()}
      data-testid="test-case-detail-last-run-badge"
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dot)} aria-hidden="true" />
      Last run · {run.status} · {new Date(run.runAt).toLocaleString()}
    </span>
  )
}

